import { useEffect, useState } from "react";
import { supabaseReservation } from "../../../../../integrations/supabase/client";
import { Button } from "../../../../ui/button";
import { Card } from "../../../../ui/card";
import { useToast } from "../../../../../hooks/use-toast";
import { Loader2, CreditCard, AlertTriangle, DollarSign, TrendingUp, History, ArrowDown, ArrowUp } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Progress } from "../../../..//ui/progress";
import { format } from "date-fns";
import { useAuth } from "../../../../../contexts/AuthContext";

interface CreditsData {
  balance_cents: number;
  balance_dollars: number;
  low_balance: boolean;
}

interface Transaction {
  id: string;
  amount_cents: number;
  type: string;
  description: string | null;
  created_at: string;
}

const CREDIT_PACKAGES = [
  { id: "small", amount: 25, label: "$25" },
  { id: "medium", amount: 50, label: "$50", popular: true },
  { id: "large", amount: 100, label: "$100" },
];

export function CreditsManager() {
  const { toast } = useToast();
  const { user, isLoading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [credits, setCredits] = useState<CreditsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  useEffect(() => {
    // Wait for auth to be loaded and user to be authenticated
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      setLoadingTransactions(false);
      return;
    }

    fetchCredits();
    fetchTransactions();
    
    // Handle payment result from URL
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      toast({
        title: "Payment Successful",
        description: "Your credits have been added to your account.",
      });
      // Refresh credits after successful payment
      setTimeout(() => {
        fetchCredits();
        fetchTransactions();
      }, 2000);
    } else if (paymentStatus === "cancelled") {
      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled.",
        variant: "destructive",
      });
    }

    // Set up realtime subscription for credit changes
    const creditsChannel = supabaseReservation
      .channel('credits-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'account_credits' },
        () => {
          fetchCredits();
        }
      )
      .subscribe();

    const transactionsChannel = supabaseReservation
      .channel('transactions-changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'credit_transactions' },
        () => {
          fetchCredits();
          fetchTransactions();
        }
      )
      .subscribe();

    return () => {
      supabaseReservation.removeChannel(creditsChannel);
      supabaseReservation.removeChannel(transactionsChannel);
    };
  }, [authLoading, user]);

  const fetchCredits = async () => {
    try {
      const { data, error } = await supabaseReservation.functions.invoke("check-credits");
      
      if (error) throw error;
      setCredits(data);
    } catch (err) {
      console.error("Error fetching credits:", err);
      toast({
        title: "Error",
        description: "Failed to load credit balance",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const { data, error } = await supabaseReservation
        .from("credit_transactions")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(10);
      
      if (error) throw error;
      setTransactions(data || []);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handlePurchase = async (packageId: string) => {
    setPurchasing(packageId);
    try {
      const { data, error } = await supabaseReservation.functions.invoke("create-checkout", {
        body: { package: packageId },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (err) {
      console.error("Error creating checkout:", err);
      toast({
        title: "Error",
        description: "Failed to start checkout process",
        variant: "destructive",
      });
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // Calculate usage percentage (assuming $50 max for display)
  const maxDisplay = 5000; // $50 in cents
  const usagePercent = credits ? Math.min((credits.balance_cents / maxDisplay) * 100, 100) : 0;

  return (
    <div className="space-y-6">
      {/* Current Balance */}
      <div className={`p-6 rounded-lg border ${credits?.low_balance ? "border-destructive bg-destructive/10" : "border-border bg-background"}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">Current Balance</span>
            </div>
            <p className="text-4xl font-bold text-foreground">
              ${credits?.balance_dollars?.toFixed(2) ?? "0.00"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Available voice credits
            </p>
          </div>
          {credits?.low_balance && (
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <span className="text-sm font-medium">Low Balance</span>
            </div>
          )}
        </div>
        
        <div className="mt-4">
          <Progress value={usagePercent} className="h-2" />
        </div>
        
        <p className="text-xs text-muted-foreground mt-3">
          Estimated ~{Math.floor((credits?.balance_cents || 0) / 120)} minutes of voice calls remaining (at ~$1.20/min)
        </p>
      </div>

      {/* Top Up Packages */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4" />
          Top Up Credits
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {CREDIT_PACKAGES.map((pkg) => (
            <Card
              key={pkg.id}
              className={`relative p-4 cursor-pointer transition-all hover:border-primary ${
                pkg.popular ? "border-primary ring-1 ring-primary" : ""
              }`}
              onClick={() => !purchasing && handlePurchase(pkg.id)}
            >
              {pkg.popular && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded-full">
                  Popular
                </span>
              )}
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">{pkg.label}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  ~{Math.floor(pkg.amount * 100 / 120)} mins
                </p>
              </div>
              <Button
                variant={pkg.popular ? "default" : "outline"}
                size="sm"
                className="w-full mt-3"
                disabled={!!purchasing}
              >
                {purchasing === pkg.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Buy
                  </>
                )}
              </Button>
            </Card>
          ))}
        </div>
      </div>

      {/* Transaction History */}
      <div className="p-4 rounded-lg bg-muted/50 border border-border">
        <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
          <History className="h-4 w-4" />
          Recent Transactions
        </h4>
        {loadingTransactions ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No transactions yet</p>
        ) : (
          <div className="space-y-2">
            {transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="flex items-center gap-3">
                  {tx.amount_cents > 0 ? (
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <ArrowUp className="h-4 w-4 text-primary" />
                    </div>
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center">
                      <ArrowDown className="h-4 w-4 text-destructive" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {tx.type === "initial" ? "Welcome Bonus" : 
                       tx.type === "topup" ? "Credit Top-up" : 
                       tx.type === "usage" ? "Voice Call Usage" : tx.type}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(tx.created_at), "MMM d, yyyy h:mm a")}
                    </p>
                  </div>
                </div>
                <span className={`text-sm font-medium ${tx.amount_cents > 0 ? "text-primary" : "text-destructive"}`}>
                  {tx.amount_cents > 0 ? "+" : ""}${(tx.amount_cents / 100).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cost Info */}
      <div className="p-4 rounded-lg bg-muted/50 border border-border">
        <h4 className="font-medium text-foreground mb-2">Pricing Details</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• Voice calls are charged at approximately <strong>$1.20 per minute</strong></li>
          <li>• This includes AI processing, voice synthesis, and phone costs</li>
          <li>• Credits never expire and roll over month to month</li>
          <li>• You'll be alerted when balance drops below $5</li>
        </ul>
      </div>
    </div>
  );
}
